import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from './cn'

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive'
type Size = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  pill?: boolean
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-accent text-[#0A0A0F] hover:bg-accent-bright shadow-glow-gold focus-visible:ring-accent/60',
  secondary:
    'bg-surface-elevated text-ink border border-line-strong hover:border-brand hover:text-brand focus-visible:ring-brand/40',
  ghost:
    'bg-transparent text-ink hover:bg-surface-elevated focus-visible:ring-brand/30',
  destructive:
    'bg-error text-white hover:opacity-90 shadow-md focus-visible:ring-error/50',
}

const sizeClasses: Record<Size, string> = {
  sm: 'h-9  px-3.5 text-sm gap-1.5',
  md: 'h-11 px-5   text-sm gap-2',
  lg: 'h-13 px-6   text-base gap-2',
}

/**
 * Compute the same class string used by `<Button>` so callers can apply it
 * to `<Link>`, `<a>`, or any other element (e.g. router navigation CTAs).
 */
export function buttonStyles({
  variant = 'secondary',
  size = 'md',
  pill = false,
  className,
}: {
  variant?: Variant
  size?: Size
  pill?: boolean
  className?: string
} = {}) {
  return cn(
    'inline-flex items-center justify-center font-medium select-none',
    'transition-all duration-150 ease-spring',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'active:scale-[0.96]',
    pill ? 'rounded-pill' : 'rounded-xl',
    sizeClasses[size],
    variantClasses[variant],
    className,
  )
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    pill = false,
    loading = false,
    leftIcon,
    rightIcon,
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={buttonStyles({ variant, size, pill, className })}
      {...rest}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        leftIcon && <span className="-ml-0.5 flex items-center">{leftIcon}</span>
      )}
      {children}
      {!loading && rightIcon && <span className="-mr-0.5 flex items-center">{rightIcon}</span>}
    </button>
  )
})
